import { Component } from "@theme/component";

const FALLBACK_HEIGHT = 220;

/**
 * A custom element wrapper for the Tolstoy Stories widget.
 * Handles smooth height animation and content loading observation.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} tolstoyEl - The tolstoy-stories element.
 *
 * @extends {Component<Refs>}
 */
export class TolstoyStoriesWrapper extends Component {
  static get observedAttributes() {
    return ["data-product-id"];
  }

  /** @type {number} */
  height = 0;

  /** @type {boolean} */
  isLoading = true;

  /** @type {string | null} */
  error = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {MutationObserver | null} */
  #mutationObserver = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #timeoutId = null;

  connectedCallback() {
    super.connectedCallback();
    this.style.height = "0px";
    this.style.transition = "height 500ms ease-out";
    this.#setupObservers();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanup();
  }

  /**
   * Called when observed attributes change.
   * @param {string} name - The attribute name.
   * @param {string | null} oldValue - The old value.
   * @param {string | null} newValue - The new value.
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (
      name === "data-product-id" &&
      oldValue !== null &&
      oldValue !== newValue
    ) {
      this.#reset();
      this.#setupObservers();
    }
  }

  /**
   * Resets the component state.
   */
  #reset() {
    this.#cleanup();
    this.height = 0;
    this.isLoading = true;
    this.error = null;
    this.style.height = "0px";
    this.classList.remove("mt-[16px]");
  }

  /**
   * Sets up mutation and resize observers for the tolstoy-stories element.
   */
  #setupObservers() {
    const el = this.refs.tolstoyEl;

    if (!el) {
      this.error = "Tolstoy widget element not found";
      console.error("Tolstoy widget error:", this.error);
      this.isLoading = false;
      return;
    }

    // Try initial measurement
    if (el.firstChild && this.#measure(el)) {
      // Already measured successfully
    } else {
      // Set up mutation observer to wait for content
      this.#mutationObserver = new MutationObserver(() => {
        if (this.#measure(el)) {
          this.#cleanupMutationObserver();
        }
      });
      this.#mutationObserver.observe(el, { childList: true, subtree: true });

      // Timeout fallback after 5 seconds
      this.#timeoutId = setTimeout(() => {
        this.#cleanupMutationObserver();
        this.error = "Tolstoy widget did not load within 5 seconds";
        console.warn(this.error);
        this.isLoading = false;
      }, 5000);
    }

    // Set up resize observer to keep height in sync
    if ("ResizeObserver" in window) {
      this.#resizeObserver = new ResizeObserver(() => {
        const measured = this.#getMeasuredHeight(el);
        if (measured > 0) {
          this.#setHeight(measured);
        }
      });
      this.#resizeObserver.observe(el);
    }
  }

  /**
   * Measures the element height and updates state.
   * @param {HTMLElement} el - The element to measure.
   * @returns {boolean} True if measurement was successful.
   */
  #measure(el) {
    const measured = this.#getMeasuredHeight(el);
    if (measured > 0) {
      this.#setHeight(measured);
      this.isLoading = false;
      return true;
    }
    // Fallback if we can't measure but element has content
    if (el.firstChild) {
      this.#setHeight(this.height > 0 ? this.height : FALLBACK_HEIGHT);
      this.isLoading = false;
      return true;
    }
    return false;
  }

  /**
   * Gets the measured height of an element.
   * @param {HTMLElement} el - The element to measure.
   * @returns {number} The measured height.
   */
  #getMeasuredHeight(el) {
    const rectH = Math.round(el.getBoundingClientRect().height || 0);
    const scrollH = el.scrollHeight || 0;
    return rectH || scrollH;
  }

  /**
   * Sets the height and updates ARIA attributes.
   * @param {number} value - The height value in pixels.
   */
  #setHeight(value) {
    this.height = value;
    this.style.height = `${value + 20}px`;
    this.setAttribute("aria-busy", this.isLoading ? "true" : "false");
    if (this.error) {
      this.setAttribute("data-error", "true");
    } else {
      this.removeAttribute("data-error");
    }
    // Add margin-top only when content is present
    if (value > 0) {
      this.classList.add("mt-[16px]");
    } else {
      this.classList.remove("mt-[16px]");
    }
  }

  /**
   * Cleans up the mutation observer and timeout.
   */
  #cleanupMutationObserver() {
    if (this.#mutationObserver) {
      this.#mutationObserver.disconnect();
      this.#mutationObserver = null;
    }
    if (this.#timeoutId) {
      clearTimeout(this.#timeoutId);
      this.#timeoutId = null;
    }
  }

  /**
   * Cleans up all observers and tries to destroy the Tolstoy widget.
   */
  #cleanup() {
    this.#cleanupMutationObserver();
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = null;
    }
    // Try to call Tolstoy's destroy method if available
    const host = this.refs.tolstoyEl;
    if (host && typeof (/** @type {any} */ (host).destroy) === "function") {
      try {
        /** @type {any} */ (host).destroy();
      } catch (err) {
        console.error("Tolstoy widget destroy error:", err);
      }
    }
  }
}

if (!customElements.get("tolstoy-stories-wrapper")) {
  customElements.define("tolstoy-stories-wrapper", TolstoyStoriesWrapper);
}
